import { useCallback, useEffect, useState, useMemo } from "react"

import { useMutation } from "@apollo/client"
import { useForm } from "react-hook-form"

import styles from "./SalonDetailSetting.module.scss"

import { useMe } from "@/lib/graphql/me"
import { useUserPermission } from "@/main/components/common_features/salon/userPermission"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import SimpleAttributeRow from "@/main/components/ui/SimpleAttributeRow/SimpleAttributeRow"
import SimpleInputLabel from "@/main/components/ui/SimpleInputLabel/SimpleInputLabel"
import { graphql } from "@/main/gql/__generated__"
import { SalonDetailForm, salonDetailResolver } from "@/utils/forms/salonDetail"

import ContentHeader from "../common/ContentHeader/ContentHeader"
import EmitModalButton from "../common/EmitModalButton/EmitModalButton"
import ModalButtons from "../common/ModalButtons/ModalButtons"
import ModalHeader from "../common/ModalHeader/ModalHeader"

const updateSalonMutation = graphql(/* GraphQL */ `
	mutation UpdateSalon($input: UpdateSalonInput!) {
		updateSalon(input: $input) {
			salon {
				id
				name
				displayName
				address
			}
		}
	}
`)

const salonNameTitle = "店舗名"
const salonDisplayNameTitle = "店舗名 (サイドバー)"
const salonAddressTitle = "店舗住所"
const salonNameExplainMessage = "請求書等に表示される店舗名"
const salonDisplayNameExplainMessages = () => {
	return (
		<div>
			<p>左側のサイドバーに表示される店舗名</p>
			<p>(請求書等に表示されることはありません)</p>
		</div>
	)
}
const salonAddressExplainMessage = "請求書などに表示される店舗の住所"

const SalonDetailSetting = () => {
	// salon data
	const { salon } = useMe()
	const salonName = salon?.name ?? ""
	const salonDisplayName = salon?.displayName ?? salonName
	const salonAddress = salon?.address ?? ""
	const { userPermission } = useUserPermission()

	const isEditSalonHidden = useMemo(() => {
		if (userPermission?.canUpdateSalon === true) {
			return false
		}
		return true
	}, [userPermission?.canUpdateSalon])

	// modal
	const [isModalOpen, setIsModalOpen] = useState(false)
	const onModalOpenChange = useCallback(() => {
		setIsModalOpen((prev) => !prev)
	}, [setIsModalOpen])

	// form
	const {
		register,
		setValue,
		handleSubmit,
		clearErrors,
		formState: { errors },
	} = useForm({
		resolver: salonDetailResolver,
		defaultValues: {
			salonName,
			salonDisplayName,
			salonAddress,
		},
	})

	useEffect(() => {
		// 取得してきた値をセット
		setValue("salonName", salonName)
		setValue("salonDisplayName", salonDisplayName)
		setValue("salonAddress", salonAddress)
	}, [salonName, salonDisplayName, salonAddress, setValue])

	useEffect(() => {
		if (!isModalOpen) {
			// formをclear
			setValue("salonName", salonName)
			setValue("salonDisplayName", salonDisplayName)
			setValue("salonAddress", salonAddress)
			clearErrors()
		}
	}, [
		isModalOpen,
		salonName,
		salonDisplayName,
		salonAddress,
		setValue,
		clearErrors,
	])

	// onSubmit
	const [mutate] = useMutation(updateSalonMutation)
	const onSubmit = async (data: SalonDetailForm) => {
		await mutate({
			variables: {
				input: {
					name: data.salonName,
					displayName: data.salonDisplayName,
					address: data.salonAddress,
				},
			},
		}).then(({ data }) => {
			// エラー回避用に書いているが、mutationが成功するなら必ず存在する
			if (!data?.updateSalon?.salon) return
			setValue("salonName", data.updateSalon.salon.name)
			setValue("salonDisplayName", data.updateSalon.salon.displayName)
			setValue("salonAddress", data.updateSalon.salon.address)
			alert("店舗情報を更新しました")
			setIsModalOpen(false)
		})
	}

	const onClickCancel = () => {
		setIsModalOpen(false)
	}

	return (
		<div>
			<ModalDialog isOpen={isModalOpen} onOpenChange={onModalOpenChange}>
				<div className={styles.modal_content}>
					<ModalHeader
						title="店舗の編集"
						description="店舗情報を編集します。"
					/>
					<form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
						<SimpleInputLabel
							title={salonNameTitle}
							optional={false}
							questionExplanation={salonNameExplainMessage}
						/>
						<input
							type="text"
							placeholder={salonNameTitle}
							{...register("salonName")}
						/>
						<div className={styles.warning}>{errors.salonName?.message}</div>

						<SimpleInputLabel
							title={salonDisplayNameTitle}
							optional={false}
							questionExplanationJSX={salonDisplayNameExplainMessages}
						/>
						<input
							type="text"
							placeholder={salonDisplayNameTitle}
							{...register("salonDisplayName")}
						/>
						<div className={styles.warning}>
							{errors.salonDisplayName?.message}
						</div>

						<SimpleInputLabel
							title={salonAddressTitle}
							optional={false}
							questionExplanation={salonAddressExplainMessage}
						/>
						<input
							type="text"
							placeholder={salonAddressTitle}
							{...register("salonAddress")}
						/>
						<div className={styles.warning}>{errors.salonAddress?.message}</div>

						<div style={{ marginBottom: "20px" }}></div>

						<ModalButtons
							okText="保存"
							cancelText="キャンセル"
							onClickCancel={onClickCancel}
						/>
					</form>
				</div>
			</ModalDialog>

			<ContentHeader
				title="店舗情報"
				description="店舗情報です。店舗名や住所が変わった場合は、必ず編集をお願いします。"
				emittableModalButton={() => (
					<EmitModalButton
						text={"店舗の編集"}
						onClick={() => setIsModalOpen(true)}
						hide={isEditSalonHidden}
					/>
				)}
			/>

			<div>
				<SimpleAttributeRow
					label={salonNameTitle}
					questionPopoverMessage={salonNameExplainMessage}
					content={salonName}
				/>
				<SimpleAttributeRow
					label={salonDisplayNameTitle}
					questionExplanationJSX={salonDisplayNameExplainMessages}
					content={salonDisplayName}
				/>
				<SimpleAttributeRow
					label={salonAddressTitle}
					questionPopoverMessage={salonAddressExplainMessage}
					content={salonAddress}
				/>
			</div>
		</div>
	)
}

export default SalonDetailSetting
